import React, { useState, useEffect } from "react"
import { graphql } from "gatsby"
import styled, { css } from "styled-components"
import { Typography } from "@material-ui/core"
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos"

import BackgroundImage from "../../components/BackgroundImage"
import Textarea from "../../components/textarea"
import useInterval from "../../utils/useInterval"

import Edges from "../../components/_layout/edges"

import * as theme from "../../theme"

export default function Hero(props) {
  const {
    hero_content,
    hero_button,
    hero_background_images,
    hero_size,
    autoPlay,
    interval,
    path,
    featuredImage
  } = props

  const [isPlaying, setIsPlaying] = useState(true)
  const [currentSlide, setCurrentSlide] = useState(0)

  const prevSlide = () => {
    if (!hero_background_images) {
      return
    }

    if (currentSlide === 0) {
      setCurrentSlide(hero_background_images.length - 1)
    } else {
      setCurrentSlide(currentSlide - 1)
    }
  }

  const nextSlide = () => {
    if (!hero_background_images) {
      return
    }
    if (currentSlide === hero_background_images.length - 1) {
      setCurrentSlide(0)
      return
    }
    if (
      hero_background_images &&
      hero_background_images.length > 1 &&
      hero_background_images.length - 1 > currentSlide
    ) {
      setCurrentSlide(currentSlide + 1)
    } else {
      return
    }
  }

  useInterval(
    () => {
      !!autoPlay && nextSlide()
    },
    isPlaying ? interval : null
  )

  useEffect(() => {
    setCurrentSlide(0)
  }, [path])

  return (
    <Container {...{ hero_size }}>
      {hero_size === "large" && hero_background_images && (
        <Controls>
          <SliderButton
            onClick={() => prevSlide()}
            dir="back"
            onMouseEnter={() => setIsPlaying(false)}
            onMouseLeave={() => setIsPlaying(true)}
          >
            <ArrowForwardIosIcon />
          </SliderButton>
          <SliderButton
            onClick={() => nextSlide()}
            dir="forward"
            onMouseEnter={() => setIsPlaying(false)}
            onMouseLeave={() => setIsPlaying(true)}
          >
            <ArrowForwardIosIcon />
          </SliderButton>
        </Controls>
      )}
      <Edges size="lg">
        <Content>
          {hero_content && (
            <HeroContent hero_button={hero_button}>
              <Textarea content={hero_content} />
            </HeroContent>
          )}

          {hero_button && (
            <HeroButton href={hero_button.url} hero_size={hero_size}>
              <Typography variant="button" color="inherit">
                {hero_button.title}
              </Typography>
            </HeroButton>
          )}
        </Content>
      </Edges>

      {currentSlide !== null &&
        hero_background_images &&
        hero_background_images.map((image, index) => {
          return (
            <Slide key={index} activeSlide={index === currentSlide}>
              <BackgroundImage bg={image.url} position="absolute" />
            </Slide>
          )
        })}

      {hero_background_images === null && !!featuredImage && (
        <Slide activeSlide={true}>
          <BackgroundImage bg={featuredImage} position="absolute" />
        </Slide>
      )}
    </Container>
  )
}
const Container = styled.div`
  ${({ hero_size }) =>
    hero_size === "large"
      ? css`
          @media (min-width: 800px) {
            height: 75vh;
          }
          min-height: 480px;
        `
      : css`
          @media (min-width: 800px) {
            height: 25vh;
          }
          min-height: 220px;
        `}

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  background: #4a4a4a;
  overflow: hidden;
  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    ${({ hero_size }) =>
      hero_size === "large" &&
      css`
        background: rgba(0, 0, 0, 0.35);
      `}
  }
`
const Content = styled.div`
  padding: 100px 0 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  position: relative;
  z-index: 1;
`

const HeroContent = styled.div`
  ${({ hero_button }) =>
    !!hero_button &&
    css`
      margin-bottom: 40px;
    `}
  && {
    color: white;
    * {
      color: inherit;
    }
    .hero-post-title {
      text-transform: uppercase;
    }
  }
`

const HeroButton = styled.a`
  position: relative;
  display: block;
  border: 1px solid #ffffff;
  padding: 15px 60px;
  background: transparent;
  color: white;
  text-decoration: none;
  text-transform: uppercase;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: white;
    color: ${theme.color.green};
  }
`

const Slide = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: all 0.5s ease-in-out;
  ${({ activeSlide }) =>
    activeSlide
      ? css`
          opacity: 1;
        `
      : css`
          opacity: 0;
        `}
`

export const query = graphql`
  fragment heroContent on WordsbyCollectionsAcf {
    hero_size
    hero_button {
      title
      url
      target
    }
    hero_background_images {
      id
      title
      filename
      alt
      url {
        publicURL
        childImageSharp {
          fluid(quality: 90, maxWidth: 2560) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
    hero_content
  }
`

const Controls = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 9999;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const SliderButton = styled.button`
  pointer-events: all;
  border: none;
  margin: 0;
  padding: 0;
  width: auto;
  overflow: visible;
  background: transparent;
  color: inherit;
  font: inherit;
  line-height: normal;
  -webkit-font-smoothing: inherit;
  -moz-osx-font-smoothing: inherit;
  -webkit-appearance: none;

  padding: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  ${({ disabled }) => (!disabled ? "color: white;" : "color: grey;")}

  ${({ dir }) =>
    dir === "back" &&
    css`
      svg {
        filter: drop-shadow(0px 0px 3px black);
        transform: rotate(180deg);
      }
    `}
`
